import React from 'react';
import './ActivityHeatmap.css';

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function isoDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function buildActivityHeatmapData(entries = [], weeks = 52) {
  const aggregated = entries.reduce((acc, entry) => {
    if (!entry?.date) return acc;
    const key = entry.date;
    const value = Number(entry.value || 0);
    acc[key] = (acc[key] || 0) + value;
    return acc;
  }, {});

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const endOfWeek = new Date(today);
  endOfWeek.setDate(today.getDate() + (6 - today.getDay()));

  const startDate = new Date(endOfWeek);
  startDate.setDate(endOfWeek.getDate() - (weeks * 7 - 1));

  const cells = [];
  for (let i = 0; i < weeks * 7; i += 1) {
    const date = new Date(startDate);
    date.setDate(startDate.getDate() + i);
    const dateKey = isoDate(date);
    cells.push({ date: dateKey, count: aggregated[dateKey] || 0 });
  }

  const maxCount = Math.max(1, ...cells.map((cell) => cell.count));

  const months = [];
  for (let week = 0; week < weeks; week += 1) {
    const index = week * 7;
    const date = new Date(cells[index].date);
    if (date.getDate() <= 7) {
      months.push({
        col: week + 1,
        label: date.toLocaleDateString(undefined, { month: 'short' }),
      });
    }
  }

  const total = cells.reduce((sum, cell) => sum + cell.count, 0);

  return { cells, maxCount, total, weeks, months };
}

function getBucket(value, maxValue) {
  if (value <= 0) return 0;
  const ratio = value / maxValue;
  if (ratio < 0.25) return 1;
  if (ratio < 0.5) return 2;
  if (ratio < 0.75) return 3;
  return 4;
}

export default function ActivityHeatmap({
  entries = [],
  weeks = 52,
  title = 'Activity',
  subtitle,
  className = '',
}) {
  const data = buildActivityHeatmapData(entries, weeks);

  return (
    <section className={`activity-heatmap ${className}`.trim()}>
      <div className="activity-heatmap__header">
        <h3 className="activity-heatmap__title">{title}</h3>
        <span className="activity-heatmap__summary">{subtitle || `${data.total} tracked activities`}</span>
      </div>

      <div
        className="activity-heatmap__calendar"
        style={{ '--activity-weeks': data.weeks }}
        role="img"
        aria-label={`${title}: ${data.total} tracked activities in the last ${weeks} weeks`}
      >
        <div className="activity-heatmap__months" aria-hidden="true">
          {data.months.map((month, idx) => (
            <span key={`${month.label}-${idx}`} style={{ gridColumnStart: month.col }}>
              {month.label}
            </span>
          ))}
        </div>

        <div className="activity-heatmap__grid">
          {data.cells.map((cell, idx) => {
            const bucket = getBucket(cell.count, data.maxCount);
            const day = WEEKDAY_LABELS[idx % 7];
            const dateLabel = new Date(cell.date).toLocaleDateString(undefined, {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            });
            const countLabel = cell.count === 1 ? '1 activity' : `${cell.count} activities`;
            return (
              <span
                key={cell.date}
                className={`activity-heatmap__cell activity-heatmap__cell--lvl${bucket}`}
                aria-label={`${dateLabel} (${day}): ${countLabel}`}
                title={`${dateLabel}: ${countLabel}`}
              />
            );
          })}
        </div>
      </div>

      <div className="activity-heatmap__legend" aria-hidden="true">
        <span>Less</span>
        <span className="activity-heatmap__cell activity-heatmap__cell--lvl0" />
        <span className="activity-heatmap__cell activity-heatmap__cell--lvl1" />
        <span className="activity-heatmap__cell activity-heatmap__cell--lvl2" />
        <span className="activity-heatmap__cell activity-heatmap__cell--lvl3" />
        <span className="activity-heatmap__cell activity-heatmap__cell--lvl4" />
        <span>More</span>
      </div>
    </section>
  );
}
